/**
 * Selection Policy API client.
 *
 * The write is a **PUT of the whole ruleset under the revision the screen loaded**, mirroring the
 * server. There is no `withholdOption(key)` helper, because offering one would imply the server
 * merges — and it does not. Two administrators narrowing the same policy are told their view is
 * stale, not silently combined (docs/engineering/06-selection-policy.md).
 */

import type {
  AccessMode,
  ItemType,
  OptionAvailability,
  PolicyLayer,
  PolicyRule,
  PolicyScope,
} from '@pius/contracts';

import { request } from '../../api/client';

export interface PolicyItem {
  item_key: string;
  label: string;
  item_type: ItemType;
  section_key: string;
  section_label: string;
  access_mode: AccessMode;
  access_decided_by: PolicyLayer;
  note?: string;
  /** Whether this Liturgy's own layer has a rule here — what makes "Reset to inherited" real. */
  overridden: boolean;
  has_options: boolean;
  options: OptionAvailability[];
  /** Approved option keys this configuration no longer offers. */
  conflicts: string[];
}

export interface PolicyRole {
  role_key: string;
  label: string;
  access_mode: AccessMode;
  in_use: boolean;
  /** A Constraint Role can be narrowed but never withdrawn. */
  is_constraint: boolean;
  allows_multiple: boolean;
  note?: string;
  overridden: boolean;
}

export interface PolicySummary {
  items: number;
  narrowed_items: number;
  withheld_options: number;
  participant_restricted_items: number;
  withdrawn_roles: number;
}

export interface PolicyConflict {
  id: string;
  item_id: string;
  item_label: string;
  withheld_value: string;
  policy_revision: number;
  detected_at: string;
}

export type OptionStance = 'withheld' | 'offered' | 'inherit';

export interface PolicyChangeEntry {
  target_kind: 'item' | 'role';
  target_key: string;
  options: { key: string; from: OptionStance; to: OptionStance }[];
  access_mode?: { from: AccessMode | 'inherit'; to: AccessMode | 'inherit' };
  withdrawn?: { from: boolean | 'inherit'; to: boolean | 'inherit' };
}

export interface PolicyHistoryEntry {
  revision: number;
  occurred_at: string;
  /** Never a name for a Capability, and never an email for an Account. */
  actor_kind: 'account' | 'capability' | 'system';
  actor_display_name: string | null;
  entries: PolicyChangeEntry[];
}

export interface LiturgyPolicyView {
  liturgy_id: string;
  title: string;
  policy_revision: number;
  policy_name: string | null;
  items: PolicyItem[];
  roles: PolicyRole[];
  summary: PolicySummary;
  conflicts: PolicyConflict[];
  history: PolicyHistoryEntry[];
}

export interface PolicyLibraryRow {
  id: string;
  name: string;
  scope: PolicyScope;
  revision: number;
  updated_at: string;
  owner_liturgy_id: string | null;
  raw_owner_id: string;
  owner_label: string;
  restricted_items: number;
  restricted_roles: number;
  withheld_options: number;
  restricted_modes: number;
}

export function fetchLiturgyPolicy(liturgyId: string): Promise<LiturgyPolicyView> {
  return request(`/liturgies/${liturgyId}/selection-policy`);
}

export function saveLiturgyPolicy(
  liturgyId: string,
  input: { expected_revision: number; rules: PolicyRule[]; name?: string },
): Promise<{ revision: number; conflicts: { id: string; item_label: string }[] }> {
  return request(`/liturgies/${liturgyId}/selection-policy`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function fetchPolicyLibrary(): Promise<{ data: PolicyLibraryRow[] }> {
  return request('/policies');
}

export function fetchOrganizationPolicy(organizationId: string): Promise<unknown> {
  return request(`/organizations/${organizationId}/selection-policy`);
}

export function saveOrganizationPolicy(
  organizationId: string,
  rules: unknown[],
  expectedRevision?: number,
): Promise<unknown> {
  return request(`/organizations/${organizationId}/selection-policy`, {
    method: 'PUT',
    body: JSON.stringify({ rules, expected_revision: expectedRevision }),
  });
}

export function fetchClericPolicy(): Promise<unknown> {
  return request('/me/selection-policy');
}

export function saveClericPolicy(rules: unknown[], expectedRevision?: number): Promise<unknown> {
  return request('/me/selection-policy', {
    method: 'PUT',
    body: JSON.stringify({ rules, expected_revision: expectedRevision }),
  });
}
